/**
 * Sursa de date pentru calitatea aerului: CAMS european sau modelul național.
 *
 * Aplicația poate arăta aceleași hărți din două surse. CAMS publică ansamblul
 * european la 0,1°; serviciul național rulează CHIMERE pe grila RO62, la 0,06°,
 * cu 15 niveluri verticale în loc de 10 și pas orar în loc de trei ore.
 *
 * Cele două diferă prin grilă, prin setul de poluanți și prin axa de timp, deci
 * comutarea reîncarcă pagina în loc să schimbe sursa din mers: altfel ar trebui
 * invalidate simultan cuburile în cache, cronologia, legenda, filtrul încrucișat
 * și volumele 3D — multe ocazii de stare inconsistentă, pentru un câștig pe care
 * utilizatorul nu-l vede, fiindcă datele sunt locale și reîncărcarea e instantanee.
 *
 * Straturile meteo nu depind de această alegere: vin din ECMWF în ambele cazuri.
 */

const CHEIE_STOCARE = 'cams-explorer:sursa-date';

export const SURSE = {
  cams: {
    id: 'cams',
    eticheta: 'CAMS Europa',
    detaliu: 'ansamblu european · ~11 km',
    descriere: 'Ansamblul european CAMS, prognoză pe 96 de ore la pas de 3 ore.',
    surface: 'cams_ro_latest.zarr',
    volume: 'cams_ro_3d_latest.zarr',
    variabile: ['pm25', 'pm10', 'no2', 'o3', 'so2', 'co'],
    variabile3d: ['pm25', 'no2'],
  },
  roncp: {
    id: 'roncp',
    eticheta: 'CAMS RO NCP',
    detaliu: 'model național CHIMERE · ~6 km',
    descriere: 'Modelul național CHIMERE v2023 pe grila RO62, prognoză pe 48 de ore la pas orar.',
    surface: 'ro62_ro_latest.zarr',
    volume: 'ro62_ro_3d_latest.zarr',
    variabile: ['pm25', 'pm10', 'no2', 'o3', 'so2', 'co', 'no', 'nox'],
    variabile3d: ['pm25', 'no2'],
    // Verticala e aproximativă — vezi RO62_LEVEL_HEIGHTS_APROX din pipeline/config.py.
    verticalaAproximativa: true,
  },
};

export const SURSA_IMPLICITA = 'cams';

/** Sursa activă. Cade pe cea implicită dacă stocarea e indisponibilă sau invalidă. */
export function sursaCurenta() {
  let id = SURSA_IMPLICITA;
  try {
    const salvat = localStorage.getItem(CHEIE_STOCARE);
    if (salvat && SURSE[salvat]) id = salvat;
  } catch {
    // Fereastră privată sau stocare blocată — rămânem pe implicit.
  }
  return SURSE[id];
}

/** Schimbă sursa și reîncarcă, dacă e într-adevăr alta. */
export function schimbaSursa(id) {
  if (!SURSE[id] || id === sursaCurenta().id) return false;
  try {
    localStorage.setItem(CHEIE_STOCARE, id);
  } catch {
    // Fără stocare nu putem reține alegerea peste reîncărcare, deci renunțăm
    // în loc să reîncărcăm pe aceeași sursă și să pară că butonul nu face nimic.
    console.warn('Stocarea locală e indisponibilă — sursa nu poate fi schimbată.');
    return false;
  }
  window.location.reload();
  return true;
}

/**
 * URL-ul unui store al sursei active.
 * @param {'surface'|'volume'} fel
 */
export function urlStore(fel, sursa = sursaCurenta()) {
  const baza = `${window.location.origin}${import.meta.env.BASE_URL}data/zarr/`;
  return baza + sursa[fel];
}

/** Store-ul există și e servit? Folosit ca să nu oferim o sursă nefuncțională. */
export async function sursaDisponibila(id) {
  const sursa = SURSE[id];
  if (!sursa) return false;
  try {
    const r = await fetch(`${urlStore('surface', sursa)}/.zgroup`, { method: 'HEAD' });
    return r.ok;
  } catch {
    return false;
  }
}

/**
 * Montează comutatorul de sursă într-un container.
 *
 * Ascunde complet comutatorul dacă modelul național nu e publicat — un buton
 * care duce la o hartă goală e mai rău decât absența lui.
 *
 * @param {HTMLElement} container
 * @param {{compact?: boolean}} optiuni
 */
export async function monteazaComutator(container, { compact = false } = {}) {
  if (!container) return;

  const activa = sursaCurenta();
  const disponibile = [];
  for (const id of Object.keys(SURSE)) {
    if (id === activa.id || await sursaDisponibila(id)) disponibile.push(id);
  }
  if (disponibile.length < 2) return;

  container.classList.add('sursa-comutator');
  if (compact) container.classList.add('sursa-compact');
  container.innerHTML = `
    <div class="sursa-titlu">Sursă date</div>
    <div class="sursa-optiuni" role="radiogroup" aria-label="Sursa datelor de calitate a aerului">
      ${disponibile.map(id => {
        const s = SURSE[id];
        const activ = id === activa.id;
        return `
          <button type="button" class="sursa-optiune${activ ? ' activ' : ''}"
                  role="radio" aria-checked="${activ}" data-sursa="${id}"
                  title="${s.descriere}">
            <span class="sursa-eticheta">${s.eticheta}</span>
            <span class="sursa-detaliu">${s.detaliu}</span>
          </button>`;
      }).join('')}
    </div>`;

  container.querySelectorAll('[data-sursa]').forEach(b => {
    b.addEventListener('click', () => schimbaSursa(b.dataset.sursa));
  });
}
